import React, { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import useAuthStore from '@/store/authStore';
import { resolvePostLoginPath } from '@/utils/sellerReturn';
import { signInGoogle } from '@/api/auth';
import { readReferralCode } from '@/utils/referral';
import styled from 'styled-components';

declare global {
   interface Window {
      google?: {
         accounts: {
            id: {
               initialize: (config: any) => void;
               renderButton: (element: HTMLElement, config: any) => void;
            };
         };
      };
   }
}

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

const GoogleSignInButton = () => {
   const buttonRef = useRef<HTMLDivElement>(null);
   const containerRef = useRef<HTMLDivElement>(null);
   const navigate = useNavigate();
   const location = useLocation();
   const { setAuth } = useAuthStore();
   const [sdkLoaded, setSdkLoaded] = useState(false);

   const { mutate } = useMutation({
      mutationFn: signInGoogle,
      onSuccess: (response) => {
         const { token, user } = response.data;

         if (
            user &&
            typeof user === 'object' &&
            typeof user.name === 'string' &&
            typeof user.email === 'string'
         ) {
            setAuth(token, user);
         } else {
            toast.error('Login failed: invalid user data');
            return;
         }

         // Honours a pending seller (?next= or the stored note) before the
         // usual dashboard landing — see utils/sellerReturn.
         navigate(resolvePostLoginPath(location.state?.from), { replace: true });
      },
      onError: (err: any) => {
         const errorMessage =
            err?.response?.data?.err ||
            err?.response?.data?.message ||
            'Google sign-in failed. Please try again.';
         toast.error(errorMessage);
      },
   });

   const handleGoogleCallback = (response: any) => {
      if (response.credential) {
         // Referral attribution (points phase 3): forward the invite code so a
         // brand-new Google account gets referredBy set, matching the
         // email/password signup. Prefer the live ?ref= param; fall back to the
         // one persisted at landing — the Google account-picker can drop the
         // URL query, but the saved code survives it.
         mutate({
            idToken: response.credential,
            referralCode: readReferralCode(),
         });
      } else {
         toast.error('No credential received from Google');
      }
   };

   // Load the Google Identity Services SDK. The bug this guards
   // against: a previous mount may have injected the <script> tag but
   // not yet executed it (async). The old code checked only for tag
   // presence, set sdkLoaded=true immediately on remount, then the
   // second effect bailed because window.google was still undefined —
   // and never retried. React 18 StrictMode (effects run twice) made
   // it happen consistently. Now we verify window.google itself is
   // ready, and if not, wait for the script's load event explicitly.
   useEffect(() => {
      let cancelled = false;

      // Already loaded? Done.
      if (window.google?.accounts?.id) {
         setSdkLoaded(true);
         return;
      }

      const markLoaded = () => {
         if (!cancelled) setSdkLoaded(true);
      };

      const existing = document.getElementById(
         'google-gsi-script'
      ) as HTMLScriptElement | null;

      if (existing) {
         // Tag already injected by a previous mount but the script
         // body may still be loading. Attach onload + a tiny poll
         // fallback in case the load event already fired before we
         // attached.
         existing.addEventListener('load', markLoaded);
         const poll = window.setInterval(() => {
            if (window.google?.accounts?.id) {
               markLoaded();
               window.clearInterval(poll);
            }
         }, 80);
         return () => {
            cancelled = true;
            existing.removeEventListener('load', markLoaded);
            window.clearInterval(poll);
         };
      }

      const script = document.createElement('script');
      script.id = 'google-gsi-script';
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = markLoaded;
      document.head.appendChild(script);

      return () => {
         cancelled = true;
      };
   }, []);

   // Initialize and render once SDK is loaded. Clears the host div
   // first so a previous render from a stale mount doesn't end up
   // stacking two buttons.
   useEffect(() => {
      if (!sdkLoaded || !window.google?.accounts?.id || !buttonRef.current)
         return;

      window.google.accounts.id.initialize({
         client_id: GOOGLE_CLIENT_ID,
         callback: handleGoogleCallback,
      });

      buttonRef.current.innerHTML = '';
      const containerWidth = containerRef.current?.offsetWidth || 400;

      window.google.accounts.id.renderButton(buttonRef.current, {
         theme: 'outline',
         size: 'large',
         width: containerWidth,
         text: 'continue_with',
      });
   }, [sdkLoaded]);

   return (
      <Wrapper ref={containerRef}>
         <div ref={buttonRef} />
      </Wrapper>
   );
};

const Wrapper = styled.div`
   width: 100%;
   margin-bottom: 10px;

   & > div {
      width: 100% !important;
   }

   & iframe {
      width: 100% !important;
   }
`;

export default GoogleSignInButton;
